import type { Product } from "@decocms/apps-commerce/types";
import SimpleShelf from "~/components/product/SimpleShelf";
import productsByCollectionId, {
  type Props as CollectionLoaderProps,
} from "~/loaders/daniela/productsByCollectionId";

/**
 * Vitrine simples por coleção: quem monta a página informa apenas o
 * **ID da coleção** (cluster VTEX) e o `loader` desta seção busca os
 * produtos automaticamente — sem precisar escolher um loader de produtos
 * no formulário do CMS. Mesmo caminho já usado por
 * `LandingPages/TabCategoriesByCollection.tsx`.
 *
 * `products` continua existindo (oculto no CMS) para não quebrar blocos
 * antigos que apontam direto para um loader de produtos: quando o
 * `collectionId` está preenchido ele tem prioridade.
 */
export interface Props {
  title?: string;
  /** @format rich-text */

  /**
   * @title Subtítulo
   * @description Subtítulo opcional abaixo do título principal
   * @format rich-text
   */
  subtitle?: string;

  /**
   * @title ID da coleção (cluster VTEX)
   * @description Preenchendo este campo, a vitrine busca sozinha os produtos da coleção
   */
  collectionId?: number;

  /**
   * @title Quantidade de produtos
   * @description Máximo de produtos buscados da coleção (padrão: 12)
   */
  count?: number;

  /**
   * @title Ordenação dos produtos
   * @description Valor de `sort` da Intelligent Search (ex: `orders:desc`,
   * `release:desc`). Vazio = ordem padrão da VTEX para o cluster.
   */
  sort?: CollectionLoaderProps["sort"];

  /**
   * Mantido para compatibilidade com blocos antigos que já apontam para um
   * loader de produtos. Ignorado quando há `collectionId`.
   * @ignore
   */
  products?: Product[] | null;

  /**
   * @title Ativar cor de Black Friday?
   * @description Ativa as cores de Black Friday e remove a de Natal
   */
  colorDefault?: boolean;

  /**
   * @title URL para "Ver Todos"
   * @description Link para página com todos os produtos
   */
  seeAllUrl?: string;

  /**
   * @title Texto do "Ver Todos"
   * @description Texto personalizado para o link "ver todos"
   * @default "ver todos"
   */
  seeAllText?: string;
}

/**
 * Loader de seção (roda no servidor, antes do render): com `collectionId`
 * preenchido, busca os produtos do cluster e injeta em `products`.
 */
export const loader = async (props: Props) => {
  if (props.collectionId === undefined || props.collectionId === null) {
    return props;
  }

  const products = await productsByCollectionId({
    collectionId: props.collectionId,
    count: props.count,
    sort: props.sort,
  });

  return { ...props, products };
};

function SimpleShelfSection({
  products,
  title,
  subtitle,
  colorDefault,
  seeAllUrl,
  seeAllText,
}: Props) {
  if (!products || !Array.isArray(products) || products.length === 0) {
    return null;
  }

  return (
    <SimpleShelf
      products={products}
      title={title}
      subtitle={subtitle}
      colorDefault={colorDefault}
      seeAllUrl={seeAllUrl}
      seeAllText={seeAllText}
    />
  );
}

// Componente de Loading para Async Rendering
// Retorna null para evitar loading infinito quando não há produtos
export const LoadingFallback = () => null;

export default SimpleShelfSection;
